import { type JSX, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet } from 'react-router';

import {
  hasPermission,
  PANEL_PERMISSIONS,
  useStaffSession,
  useStaffTokenStore,
} from '@/entities/staff-session';
import { useStaffLogout } from '@/features/staff-login';
import { adminPath } from '@/shared/config';
import { Button, Card, ErrorState, ShieldIcon, Skeleton } from '@/shared/ui';

/**
 * Two different failures with two different answers: not signed in sends you to
 * the login screen, signed in without any panel permission gets a clear refusal
 * rather than a blank page. Neither is a security control — the server checks
 * the same permission in its Application layer (CLAUDE.md §3.3).
 */
export function AdminGuard(): JSX.Element {
  const { t } = useTranslation(['auth', 'common']);
  const token = useStaffTokenStore((state) => state.token);
  const setToken = useStaffTokenStore((state) => state.setToken);
  const session = useStaffSession();
  const logout = useStaffLogout();
  const expired = session.error?.status === 401;

  // synchronizing with the stored token: the server has just told us it is dead
  useEffect(() => {
    if (expired) {
      setToken(null);
    }
  }, [expired, setToken]);

  if (token === null || expired) {
    return <Navigate replace to={adminPath('/login')} />;
  }

  if (session.isPending) {
    return (
      <div className="mx-auto flex w-full max-w-4xl flex-col gap-4 px-4 py-12">
        <Skeleton className="h-12" label={t('common:states.loading')} />
        <Skeleton className="h-64" label={t('common:states.loading')} />
      </div>
    );
  }

  if (session.error !== null) {
    return (
      <Card className="mx-auto mt-12 w-full max-w-md">
        <ErrorState
          error={session.error}
          onRetry={(): void => {
            void session.refetch();
          }}
        />
      </Card>
    );
  }

  // any one section is enough to open the panel; each section checks its own
  // permission, and the server checks it again (CLAUDE.md §3.3)
  if (!PANEL_PERMISSIONS.some((permission) => hasPermission(session.data, permission))) {
    return (
      <Card className="mx-auto mt-12 w-full max-w-md">
        <Card.Body className="flex flex-col items-center gap-4 pt-6 text-center">
          <ShieldIcon className="size-8 text-muted" />
          <h1 className="type-title text-strong">{t('auth:staff.noPermissionTitle')}</h1>
          <p className="type-body text-muted">{t('auth:staff.noPermission')}</p>
          <Button
            loading={logout.isPending}
            onClick={(): void => {
              logout.mutate(undefined);
            }}
            variant="secondary"
          >
            {t('auth:logout')}
          </Button>
        </Card.Body>
      </Card>
    );
  }

  return <Outlet />;
}
